<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Company Scheduling</h1>
            <p class="text-slate-400">Manage employee schedules, shift assignments, and work planning</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="showCreateShiftModal = true" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              Create Shift
            </button>
            <button @click="showTimeOffModal = true" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Request Time Off
            </button>
          </div>
        </div>
      </div>

      <!-- Scheduling Stats -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Scheduled Employees</p>
            <p class="text-3xl font-bold text-white">{{ scheduleStats.scheduledEmployees }}</p>
            <p class="text-sm text-emerald-400">this week</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Hours</p>
            <p class="text-3xl font-bold text-white">{{ scheduleStats.totalHours }}</p>
            <p class="text-sm text-cyan-400">this week</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Pending Requests</p>
            <p class="text-3xl font-bold text-white">{{ scheduleStats.pendingRequests }}</p>
            <p class="text-sm text-amber-400">time off</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Coverage Rate</p>
            <p class="text-3xl font-bold text-white">{{ scheduleStats.coverageRate }}%</p>
            <p class="text-sm text-purple-400">shifts covered</p>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mb-8">
        <h2 class="text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button @click="showCreateShiftModal = true" class="quick-action-card">
            <svg class="w-8 h-8 text-emerald-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            <span class="text-sm font-medium text-white">Create Shift</span>
          </button>
          
          <button @click="showSwapShiftModal = true" class="quick-action-card">
            <svg class="w-8 h-8 text-cyan-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
            </svg>
            <span class="text-sm font-medium text-white">Swap Shift</span>
          </button>
          
          <button @click="showTimeOffModal = true" class="quick-action-card">
            <svg class="w-8 h-8 text-amber-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="text-sm font-medium text-white">Request Time Off</span>
          </button>
          
          <button @click="generateScheduleReport" class="quick-action-card">
            <svg class="w-8 h-8 text-purple-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <span class="text-sm font-medium text-white">Generate Report</span>
          </button>
        </div>
      </div>

      <!-- Calendar View -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Weekly Schedule -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-semibold text-white">Weekly Schedule</h2>
              <div class="flex items-center space-x-2">
                <button @click="previousWeek" class="btn-secondary">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                  </svg>
                </button>
                <span class="text-sm text-slate-300">{{ currentWeekRange }}</span>
                <button @click="nextWeek" class="btn-secondary">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>

            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-slate-700/50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Employee</th>
                    <th v-for="day in weekDays" :key="day.date" class="px-4 py-3 text-center text-xs font-medium text-slate-300 uppercase tracking-wider">
                      {{ day.name }}<br>
                      <span class="text-xs text-slate-400">{{ formatDate(day.date) }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-700/50">
                  <tr v-for="employee in employees" :key="employee.id" class="hover:bg-slate-700/30">
                    <td class="px-4 py-3 whitespace-nowrap text-sm text-white">{{ employee.name }}</td>
                    <td v-for="day in weekDays" :key="day.date" class="px-4 py-3 text-center">
                      <div v-if="getShiftForEmployee(employee.id, day.date)" class="text-xs">
                        <div class="bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                          {{ getShiftForEmployee(employee.id, day.date).startTime }}-{{ getShiftForEmployee(employee.id, day.date).endTime }}
                        </div>
                        <button
                          class="mt-1 text-red-400 hover:text-red-300"
                          @click="deleteShift(getShiftForEmployee(employee.id, day.date))"
                        >Delete</button>
                      </div>
                      <div v-else class="text-xs text-slate-500">-</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Schedule Summary -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Schedule Summary</h3>
            
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Total Shifts</span>
                <span class="text-sm font-medium text-white">{{ scheduleSummary.totalShifts }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Scheduled Hours</span>
                <span class="text-sm font-medium text-emerald-400">{{ scheduleSummary.scheduledHours }}h</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Open Positions</span>
                <span class="text-sm font-medium text-amber-400">{{ scheduleSummary.openPositions }}</span>
              </div>
              <div class="border-t border-slate-700/50 pt-4">
                <div class="flex items-center justify-between">
                  <span class="text-lg font-semibold text-white">Coverage</span>
                  <span class="text-lg font-bold text-emerald-400">{{ scheduleSummary.coverage }}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Upcoming Shifts -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mt-6">
            <h3 class="text-lg font-semibold text-white mb-4">Upcoming Shifts</h3>
            
            <div class="space-y-3">
              <div v-for="shift in upcomingShifts" :key="shift.id" class="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                <div>
                  <p class="text-sm font-medium text-white">{{ shift.employee }}</p>
                  <p class="text-xs text-slate-400">{{ formatDate(shift.date) }} • {{ shift.startTime }}-{{ shift.endTime }}</p>
                </div>
                <span :class="[
                  'px-2 py-1 rounded-full text-xs font-medium',
                  shift.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                  shift.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-red-500/20 text-red-400'
                ]">
                  {{ shift.status }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Time Off Requests -->
      <div class="mt-8">
        <h2 class="text-xl font-semibold text-white mb-6">Time Off Requests</h2>
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Employee</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Type</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Start Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">End Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="request in timeOffRequests" :key="request.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">{{ request.employee }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ request.type }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(request.startDate) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(request.endDate) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      request.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                      request.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    ]">
                      {{ request.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center space-x-2">
                      <button @click="approveTimeOff(request.id)" class="text-emerald-400 hover:text-emerald-300">
                        Approve
                      </button>
                      <button @click="rejectTimeOff(request.id)" class="text-red-400 hover:text-red-300">
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Create Shift Modal -->
      <div v-if="showCreateShiftModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Create Shift</h3>
          
          <form @submit.prevent="submitShift" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Employee</label>
                <select v-model="shiftData.employee" class="form-select w-full" required>
                  <option value="">Select Employee</option>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.name">{{ employee.name }}</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Date</label>
                <input v-model="shiftData.date" type="date" class="form-input w-full" required />
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Start Time</label>
                <input v-model="shiftData.startTime" type="time" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">End Time</label>
                <input v-model="shiftData.endTime" type="time" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Position/Role</label>
              <input v-model="shiftData.position" type="text" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Notes</label>
              <textarea v-model="shiftData.notes" class="form-input w-full" rows="3"></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showCreateShiftModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Create Shift
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Request Time Off Modal -->
      <div v-if="showTimeOffModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Request Time Off</h3>
          
          <form @submit.prevent="submitTimeOffRequest" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Employee</label>
                <select v-model="timeOffData.employee" class="form-select w-full" required>
                  <option value="">Select Employee</option>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.name">{{ employee.name }}</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Type</label>
                <select v-model="timeOffData.type" class="form-select w-full" required>
                  <option value="">Select Type</option>
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
                <input v-model="timeOffData.startDate" type="date" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">End Date</label>
                <input v-model="timeOffData.endDate" type="date" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Reason</label>
              <textarea v-model="timeOffData.reason" class="form-input w-full" rows="3" required></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showTimeOffModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Submit Request
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Swap Shift Modal -->
      <div v-if="showSwapShiftModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Swap Shift</h3>
          
          <form @submit.prevent="submitShiftSwap" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">From Employee</label>
                <select v-model="swapData.fromEmployee" class="form-select w-full" required>
                  <option value="">Select Employee</option>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.name">{{ employee.name }}</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">To Employee</label>
                <select v-model="swapData.toEmployee" class="form-select w-full" required>
                  <option value="">Select Employee</option>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.name">{{ employee.name }}</option>
                </select>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Shift Date</label>
              <input v-model="swapData.shiftDate" type="date" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Reason</label>
              <textarea v-model="swapData.reason" class="form-input w-full" rows="3" required></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showSwapShiftModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Request Swap
              </button>
            </div>
          </form>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const userStore = useUserStore()

// Reactive data
const showCreateShiftModal = ref(false)
const showTimeOffModal = ref(false)
const showSwapShiftModal = ref(false)
const currentWeek = ref(new Date())

const scheduleStats = ref({
  scheduledEmployees: 0,
  totalHours: 0,
  pendingRequests: 0,
  coverageRate: 0
})

const employees = ref([])
const shifts = ref([])
const upcomingShifts = ref([])
const timeOffRequests = ref([])

const shiftData = ref({
  employee: '',
  date: '',
  startTime: '',
  endTime: '',
  position: '',
  notes: ''
})

const timeOffData = ref({
  employee: '',
  type: '',
  startDate: '',
  endDate: '',
  reason: ''
})

const swapData = ref({
  fromEmployee: '',
  toEmployee: '',
  shiftDate: '',
  reason: ''
})

// Computed
const weekDays = computed(() => {
  const days = []
  const startOfWeek = new Date(currentWeek.value)
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay())
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(startOfWeek)
    date.setDate(startOfWeek.getDate() + i)
    days.push({
      name: date.toLocaleDateString('en-US', { weekday: 'short' }),
      date: date.toISOString().split('T')[0]
    })
  }
  return days
})

const currentWeekRange = computed(() => {
  const start = new Date(currentWeek.value)
  start.setDate(start.getDate() - start.getDay())
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  
  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`
})

const scheduleSummary = computed(() => {
  const totalShifts = shifts.value.length
  const scheduledHours = shifts.value.reduce((sum, shift) => {
    const start = new Date(`2000-01-01T${shift.startTime}`)
    const end = new Date(`2000-01-01T${shift.endTime}`)
    return sum + (end - start) / (1000 * 60 * 60)
  }, 0)
  const openPositions = 0 // This would be calculated based on requirements
  const coverage = totalShifts > 0 ? Math.round((totalShifts / (totalShifts + openPositions)) * 100) : 0
  
  return {
    totalShifts,
    scheduledHours: Math.round(scheduledHours),
    openPositions,
    coverage
  }
})

// Methods
const fetchScheduleStats = async () => {
  try {
    const response = await fetch('/api/company/schedule-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      scheduleStats.value = data
    }
  } catch (error) {
    console.error('Error fetching schedule stats:', error)
  }
}

const fetchEmployees = async () => {
  try {
    const response = await fetch('/api/company/employees', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      employees.value = data.employees || []
    }
  } catch (error) {
    console.error('Error fetching employees:', error)
  }
}

const fetchShifts = async () => {
  try {
    const response = await fetch('/api/company/shifts', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      shifts.value = data.shifts || []
    }
  } catch (error) {
    console.error('Error fetching shifts:', error)
  }
}

const fetchUpcomingShifts = async () => {
  try {
    const response = await fetch('/api/company/upcoming-shifts', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      upcomingShifts.value = data.shifts || []
    }
  } catch (error) {
    console.error('Error fetching upcoming shifts:', error)
  }
}

const fetchTimeOffRequests = async () => {
  try {
    const response = await fetch('/api/company/time-off-requests', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      timeOffRequests.value = data.requests || []
    }
  } catch (error) {
    console.error('Error fetching time off requests:', error)
  }
}

const submitShift = async () => {
  try {
    const response = await fetch('/api/company/create-shift', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(shiftData.value)
    })
    
    if (response.ok) {
      showCreateShiftModal.value = false
      shiftData.value = { employee: '', date: '', startTime: '', endTime: '', position: '', notes: '' }
      await fetchShifts()
      await fetchScheduleStats()
    }
  } catch (error) {
    console.error('Error creating shift:', error)
  }
}

const deleteShift = async (shift: any) => {
  if (!shift || !window.confirm(`Delete this shift for ${shift.employee || 'the employee'}?`)) return
  try {
    const response = await fetch(`/api/company/shifts/${shift.id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || 'Delete failed')
    await fetchShifts()
  } catch (error) {
    console.error('Error deleting shift:', error)
  }
}

const submitTimeOffRequest = async () => {
  try {
    const response = await fetch('/api/company/request-time-off', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(timeOffData.value)
    })
    
    if (response.ok) {
      showTimeOffModal.value = false
      timeOffData.value = { employee: '', type: '', startDate: '', endDate: '', reason: '' }
      await fetchTimeOffRequests()
      await fetchScheduleStats()
    }
  } catch (error) {
    console.error('Error submitting time off request:', error)
  }
}

const submitShiftSwap = async () => {
  try {
    const response = await fetch('/api/company/request-shift-swap', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(swapData.value)
    })
    
    if (response.ok) {
      showSwapShiftModal.value = false
      swapData.value = { fromEmployee: '', toEmployee: '', shiftDate: '', reason: '' }
    }
  } catch (error) {
    console.error('Error submitting shift swap:', error)
  }
}

const approveTimeOff = async (requestId: number) => {
  try {
    const response = await fetch('/api/company/approve-time-off', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ requestId })
    })
    
    if (response.ok) {
      await fetchTimeOffRequests()
      await fetchScheduleStats()
    }
  } catch (error) {
    console.error('Error approving time off:', error)
  }
}

const rejectTimeOff = async (requestId: number) => {
  try {
    const response = await fetch('/api/company/reject-time-off', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ requestId })
    })
    
    if (response.ok) {
      await fetchTimeOffRequests()
      await fetchScheduleStats()
    }
  } catch (error) {
    console.error('Error rejecting time off:', error)
  }
}

const getShiftForEmployee = (employeeId: number, date: string) => {
  return shifts.value.find(shift => 
    shift.employeeId === employeeId && shift.date === date
  )
}

const previousWeek = () => {
  currentWeek.value.setDate(currentWeek.value.getDate() - 7)
}

const nextWeek = () => {
  currentWeek.value.setDate(currentWeek.value.getDate() + 7)
}

const generateScheduleReport = () => {
  // Generate schedule report
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchScheduleStats(),
    fetchEmployees(),
    fetchShifts(),
    fetchUpcomingShifts(),
    fetchTimeOffRequests()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.quick-action-card {
  @apply flex flex-col items-center justify-center p-4 bg-slate-700/30 rounded-xl hover:bg-slate-600/50 transition-colors;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
